import { useEffect, useRef, useState } from "react";
import { Html5QrcodeScanner, Html5QrcodeScanType } from "html5-qrcode";
import { CheckCircle, XCircle, Camera, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface QrScannerProps {
  onScan: (result: string) => void;
  onError?: (error: string) => void;
  showFeedback?: boolean;
  autoReset?: boolean;
  resetDelay?: number;
  className?: string;
}

/**
 * Componente de escáner QR optimizado para dispositivos móviles
 *
 * Incluye feedback visual, manejo de errores y controles táctiles
 * optimizados para pantallas pequeñas.
 */
export function QrScanner({
  onScan,
  // onError,
  showFeedback = true,
  autoReset = true,
  resetDelay = 3000,
  className,
}: QrScannerProps) {
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    message: string;
    data?: string;
  } | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Configuración del scanner optimizada para mobile
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
      supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
      // Configuraciones adicionales para mobile
      disableFlip: false,
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
      },
    };

    // Crear instancia del scanner
    scannerRef.current = new Html5QrcodeScanner(
      containerRef.current.id,
      config,
      false
    );

    // Configurar callbacks
    scannerRef.current.render(
      (decodedText) => {
        handleScanSuccess(decodedText);
      },
      (errorMessage) => {
        // Ignorar errores de decodificación durante el escaneo
        if (!errorMessage.includes("NotFoundException")) {
          console.warn("QR Scanner error:", errorMessage);
        }
      }
    );

    setIsScanning(true);

    // Cleanup al desmontar
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    };
  }, []);

  const handleScanSuccess = (decodedText: string) => {
    setIsScanning(false);

    const result = {
      success: true,
      message: "QR escaneado exitosamente",
      data: decodedText,
    };

    setLastResult(result);
    onScan(decodedText);

    // Auto reset después del delay
    if (autoReset) {
      setTimeout(() => {
        setLastResult(null);
        setIsScanning(true);
        // Reiniciar scanner
        if (scannerRef.current && containerRef.current) {
          scannerRef.current.render(
            (decodedText) => handleScanSuccess(decodedText),
            (errorMessage) => {
              if (!errorMessage.includes("NotFoundException")) {
                console.warn("QR Scanner error:", errorMessage);
              }
            }
          );
        }
      }, resetDelay);
    }
  };

  const handleManualReset = () => {
    setLastResult(null);
    setCameraError(null);
    setIsScanning(true);

    // Reiniciar scanner
    if (scannerRef.current && containerRef.current) {
      scannerRef.current.render(
        (decodedText) => handleScanSuccess(decodedText),
        (errorMessage) => {
          if (!errorMessage.includes("NotFoundException")) {
            console.warn("QR Scanner error:", errorMessage);
          }
        }
      );
    }
  };

  // const handleCameraError = (error: string) => {
  //   setCameraError(error);
  //   setIsScanning(false);
  //   if (onError) {
  //     onError(error);
  //   }
  // };

  return (
    <div className={cn("relative w-full", className)}>
      {/* Contenedor del scanner */}
      <div
        ref={containerRef}
        id="qr-scanner-container"
        className="w-full max-w-md mx-auto"
      />

      {/* Overlay de resultado */}
      {showFeedback && lastResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
          <div
            className={cn(
              "p-6 sm:p-8 rounded-lg shadow-xl text-center max-w-sm w-full mx-auto",
              lastResult.success
                ? "bg-primary/10 border-2 border-primary/20"
                : "bg-destructive/10 border-2 border-destructive/20"
            )}
          >
            <div className="mb-4">
              {lastResult.success ? (
                <CheckCircle className="h-16 w-16 text-primary mx-auto" />
              ) : (
                <XCircle className="h-16 w-16 text-destructive mx-auto" />
              )}
            </div>

            <h3
              className={cn(
                "text-xl font-bold mb-2",
                lastResult.success ? "text-primary" : "text-destructive"
              )}
            >
              {lastResult.success ? "¡QR Válido!" : "Error"}
            </h3>

            <p
              className={cn(
                "text-sm mb-6",
                lastResult.success ? "text-primary/80" : "text-destructive/80"
              )}
            >
              {lastResult.message}
            </p>

            {!autoReset && (
              <Button
                onClick={handleManualReset}
                variant="outline"
                className="w-full h-12 text-base font-medium hover:bg-accent hover:text-accent-foreground"
              >
                Escanear Otro QR
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Error de cámara */}
      {cameraError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
          <div className="p-6 sm:p-8 rounded-lg shadow-xl text-center max-w-sm w-full mx-auto bg-secondary/10 border-2 border-secondary/20">
            <div className="mb-4">
              <AlertCircle className="h-16 w-16 text-secondary mx-auto" />
            </div>

            <h3 className="text-xl font-bold mb-2 text-secondary">
              Error de Cámara
            </h3>

            <p className="text-sm mb-6 text-secondary/80">{cameraError}</p>

            <Button
              onClick={handleManualReset}
              variant="outline"
              className="w-full h-12 text-base font-medium hover:bg-accent hover:text-accent-foreground"
            >
              Reintentar
            </Button>
          </div>
        </div>
      )}

      {/* Indicador de estado */}
      {isScanning && !lastResult && !cameraError && (
        <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
          <Camera className="h-4 w-4 animate-pulse text-primary" />
          <span className="hidden sm:inline">Escaneando...</span>
          <span className="sm:hidden">Escan...</span>
        </div>
      )}
    </div>
  );
}
